import { readMinSupportedBuild, type GatePlatform } from '@powerful/data';
import { useEffect, useState } from 'react';

import { getSupabaseClient } from '../lib/supabase';

export interface VersionGateInput {
  platform: GatePlatform;
  /** Build instalado. Em nativo vem de `@capacitor/app`; em web, do build da aplicação. */
  build: number;
}

export interface VersionGateState {
  /** `true` apenas quando há certeza de que o build instalado é antigo demais. */
  blocked: boolean;
  /** `true` quando a verificação terminou — bem ou mal sucedida. */
  checked: boolean;
  /** Chave i18n da mensagem a mostrar no ecrã bloqueante. */
  messageKey: string | null;
}

/**
 * Gate de versão mínima suportada (PRD §10.15, RNF-MAINT-04).
 *
 * O app fala direto com o Postgres, sem camada de API traduzindo entre versões: o schema é o
 * contrato com todas as versões instaladas. Este hook é o que permitirá, no futuro, cortar
 * builds que já não conseguem ler o schema.
 *
 * **Falha aberta por desenho.** Qualquer resultado inconclusivo — rede fora, Supabase
 * indisponível, chave ausente, valor inválido — deixa o app abrir. O gate protege contra schema
 * incompatível, não contra falta de rede; travar o app quando a leitura falha transformaria uma
 * indisponibilidade do backend numa indisponibilidade total do produto.
 */
export function useVersionGate({ platform, build }: VersionGateInput): VersionGateState {
  const [state, setState] = useState<VersionGateState>({
    blocked: false,
    checked: false,
    messageKey: null,
  });

  useEffect(() => {
    let active = true;

    async function check(): Promise<void> {
      try {
        const minimum = await readMinSupportedBuild(getSupabaseClient(), platform);
        if (!active) return;

        setState({
          blocked: minimum !== null && build < minimum.build,
          checked: true,
          messageKey: minimum?.messageKey ?? null,
        });
      } catch {
        // Falha aberta: ver o bloco de documentação acima.
        if (active) setState({ blocked: false, checked: true, messageKey: null });
      }
    }

    void check();

    return () => {
      active = false;
    };
  }, [platform, build]);

  return state;
}
